import { useCallback, useMemo, useState } from 'react';
import { CreativeInfoItem } from '@blisspointmedia/bpm-types/dist/Creative';
import * as P from '@blisspointmedia/bpm-types/dist/Performance';
import { BsEye } from 'react-icons/bs';
import { MdRefresh } from 'react-icons/md';
import { Page } from '@Components/Page';
import { PAGE_NAMES } from '@Components/Page/page-constants';
import { EmptyState } from '@Components/EmptyState';
import { Spinner } from '@Components/Spinner';
import { KpiPicker } from '@Components/KpiPicker';
import { useCompanyInfo } from '@src/utils/hooks/useCompanyInfo';
import { useAppSelector } from '@src/redux';
import { useCreativeMap } from '@src/redux/creative';
import * as UserRedux from '@src/redux/user';
import { AttributesChart } from './AttributesChart';
import { TagsChart } from './TagsChart';
import { CreativeShelf } from './CreativeShelf';
import { CreativeInsightsData, Selection, TEST_COMPANY } from './creative-insights-constants';
import {
  buildCreativeInfoList,
  getCreativeKey,
  getFillColors,
  prepareCreativeList
} from './creative-insights-utils';
import {
  SelectionActions,
  toCompanyParam,
  useB2BCreativeTags,
  useConceptMap,
  useDemoCreativeList,
  useEnabledKpis,
  useImportanceData,
  useInitialKpi,
  useIsciData,
  useKpiPickerMap,
  useSelection,
  useSuppressedAttributes,
  useSurfaceQueryErrors
} from './use-creative-insights';
import './CreativeInsights.scss';

// Keyed on the company so a company switch starts from a fresh KPI and selection.
export const CreativeInsights = () => {
  const { cid: company } = useCompanyInfo();
  return <CreativeInsightsPage key={company} company={company} />;
};

interface CreativeInsightsPageProps {
  company: string;
}

const CreativeInsightsPage = ({ company }: CreativeInsightsPageProps) => {
  const isInternal = useAppSelector(UserRedux.isInternalSelector);
  const companyParam = toCompanyParam(company);

  const { selection, actions } = useSelection();
  const [pickedKpi, setPickedKpi] = useState<string>();

  const enabledKpisQuery = useEnabledKpis(company, isInternal);
  const kpiPickerMapQuery = useKpiPickerMap(company, enabledKpisQuery.data?.viewableKpis);
  const initialKpiQuery = useInitialKpi(company);
  const kpi = pickedKpi ?? initialKpiQuery.data ?? '';

  const isciDataQuery = useIsciData(company, kpi);
  const importanceQuery = useImportanceData(company, kpi);
  const suppressedAttributesQuery = useSuppressedAttributes(company);
  const conceptMapQuery = useConceptMap(company);
  const b2bCreativeTagsQuery = useB2BCreativeTags(company);
  const demoCreativeListQuery = useDemoCreativeList(company);

  useSurfaceQueryErrors([
    { error: initialKpiQuery.error },
    { error: importanceQuery.error, prefix: "Couldn't fetch creative insights data." },
    { error: isciDataQuery.error, prefix: "Couldn't fetch creative spend data." },
    { error: enabledKpisQuery.error },
    { error: kpiPickerMapQuery.error },
    { error: suppressedAttributesQuery.error },
    { error: conceptMapQuery.error },
    { error: b2bCreativeTagsQuery.error },
    { error: demoCreativeListQuery.error }
  ]);

  const isciData = useMemo(() => isciDataQuery.data ?? {}, [isciDataQuery.data]);
  const attributeImportanceData = useMemo(
    () => importanceQuery.data?.attributeImportanceData ?? [],
    [importanceQuery.data]
  );
  const suppressedAttributes = useMemo(
    () => suppressedAttributesQuery.data ?? new Set<string>(),
    [suppressedAttributesQuery.data]
  );
  const conceptMap = useMemo(() => conceptMapQuery.data ?? {}, [conceptMapQuery.data]);
  const b2bCreativeTags = useMemo(
    () => b2bCreativeTagsQuery.data ?? {},
    [b2bCreativeTagsQuery.data]
  );

  const fillColors = useMemo(
    () => getFillColors(attributeImportanceData, suppressedAttributes),
    [attributeImportanceData, suppressedAttributes]
  );

  const { creativeMap } = useCreativeMap({
    company: companyParam,
    streaming: false,
    mediaTypes: ['linear']
  });

  const sortedCreativeList = useMemo(() => {
    if (company === TEST_COMPANY) {
      return demoCreativeListQuery.data ?? [];
    }
    return creativeMap ? prepareCreativeList(creativeMap, b2bCreativeTags) : [];
  }, [b2bCreativeTags, creativeMap, company, demoCreativeListQuery.data]);

  const creativeInfoList = useMemo(
    () =>
      selection.attribute
        ? buildCreativeInfoList(selection.attribute, sortedCreativeList, conceptMap, isciData)
        : [],
    [selection.attribute, sortedCreativeList, conceptMap, isciData]
  );

  const selectedCreative = creativeInfoList.find(
    creative => getCreativeKey(creative) === selection.creativeKey
  );

  const handleKpiChange = useCallback(
    (value: string) => {
      if (value !== kpi) {
        setPickedKpi(value);
        actions.clearAttribute();
      }
    },
    [kpi, actions]
  );

  const data: CreativeInsightsData = {
    attributeImportanceData,
    tagImportanceData: importanceQuery.data?.tagImportanceData ?? {},
    attributeTypeBinomial: importanceQuery.data?.attributeTypeBinomial ?? {},
    suppressedAttributes,
    fillColors,
    creativeInfoList
  };

  return (
    <Page
      className='creative-insights'
      title={PAGE_NAMES.CREATIVE_INSIGHTS}
      pageType={PAGE_NAMES.CREATIVE_INSIGHTS}
      isLoading={importanceQuery.isLoading || isciDataQuery.isLoading}
      actions={
        <CreativeInsightsActions
          lastModified={importanceQuery.data?.lastModified}
          isInternalKpi={Boolean(enabledKpisQuery.data?.internalKpis.has(kpi))}
          company={companyParam}
          kpi={kpi}
          kpiMap={kpiPickerMapQuery.data ?? {}}
          onKpiChange={handleKpiChange}
        />
      }
    >
      <CreativeInsightsBody
        data={data}
        hasData={Boolean(attributeImportanceData.length && Object.keys(isciData).length)}
        selection={selection}
        selectedCreative={selectedCreative}
        actions={actions}
        kpi={kpi}
        isPending={
          initialKpiQuery.isPending ||
          (Boolean(kpi) && (importanceQuery.isPending || isciDataQuery.isPending))
        }
        isError={initialKpiQuery.isError || importanceQuery.isError || isciDataQuery.isError}
      />
    </Page>
  );
};

interface CreativeInsightsActionsProps {
  lastModified: string | undefined;
  isInternalKpi: boolean;
  company: string;
  kpi: string;
  kpiMap: P.GetKpiPickerMapResponse;
  onKpiChange: (kpi: string) => void;
}

const CreativeInsightsActions = ({
  lastModified,
  isInternalKpi,
  company,
  kpi,
  kpiMap,
  onKpiChange
}: CreativeInsightsActionsProps) => (
  <div className='creative-insights-actions'>
    {lastModified && (
      <div className='last-modified'>
        <MdRefresh className='refresh-icon' />
        <div
          className='last-modified-date'
          data-testid='last-modified'
        >{`Data Last Updated: ${lastModified}`}</div>
      </div>
    )}
    {isInternalKpi && (
      <div className='eye'>
        <BsEye />
      </div>
    )}
    {kpi && (
      <KpiPicker company={company} kpi={kpi} onChange={onKpiChange} kpiMap={kpiMap} noBreak />
    )}
  </div>
);

interface CreativeInsightsBodyProps {
  data: CreativeInsightsData;
  hasData: boolean;
  selection: Selection;
  selectedCreative: CreativeInfoItem | undefined;
  actions: SelectionActions;
  kpi: string;
  isPending: boolean;
  isError: boolean;
}

const CreativeInsightsBody = ({
  data,
  hasData,
  selection,
  selectedCreative,
  actions,
  kpi,
  isPending,
  isError
}: CreativeInsightsBodyProps) => {
  if (isError) {
    return <EmptyState title="Couldn't load creative insights" />;
  }
  if (isPending) {
    return <Spinner />;
  }
  if (!hasData) {
    return <EmptyState title='No creative insights data for this KPI' />;
  }

  return (
    <div className='creative-insights-body' data-testid='creative-insights-body'>
      <div className='creative-insights-charts'>
        <AttributesChart
          data={data}
          selectedAttribute={selection.attribute}
          onAttributeClick={actions.toggleAttribute}
          kpi={kpi}
        />
        <TagsChart data={data} selection={selection} actions={actions} kpi={kpi} />
      </div>
      <CreativeShelf
        data={data}
        selection={selection}
        selectedCreative={selectedCreative}
        actions={actions}
      />
    </div>
  );
};
